// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React from 'react';
import { IStackStyles, Spinner, Stack } from '@fluentui/react';
import { LargeRoundedButton } from './LargeRoundedButton';

export const LandingPageButtons = (props: {
  onStartCallClick: () => Promise<void>;
  onJoinFromLinkClick: () => void;
  onSpeakToBotClick: () => Promise<void>;
  onPSTNCallClick: () => void;
}): JSX.Element => {
  // startingCall is the time when the button is clicked, until the onStartCallClick callback completes.
  // During this we show the button text as a spinner.
  const [startingCall, setStartingCall] = React.useState(false);
  const startCallClicked = async (): Promise<void> => {
    // Set spinner to show while starting call
    setStartingCall(true);
    try {
      await props.onStartCallClick();
    } finally {
      // Clear spinner now onStartCallClick has completed
      setStartingCall(false);
    }
  };

  const [loadingSpeakToBot, setLoadingSpeakingToBot] = React.useState(false);
  const speakToBotClick = async (): Promise<void> => {
    // Set spinner to show while starting call
    setLoadingSpeakingToBot(true);
    try {
      await props.onSpeakToBotClick();
    } finally {
      // Clear spinner now onStartCallClick has completed
      setLoadingSpeakingToBot(false);
    }
  };

  return (
    <Stack horizontal tokens={{ childrenGap: '0.75rem' }} styles={landingPageButtonsContainerStyles}>
      <Stack.Item>
        <LargeRoundedButton
          text={startingCall ? undefined : 'Start a call'}
          iconProps={startingCall ? undefined : { iconName: 'StartCallCamera' }}
          onClick={startCallClicked}
        >
          {startingCall && <Spinner ariaLabel="Starting ACS Call" />}
        </LargeRoundedButton>
      </Stack.Item>
      <Stack.Item>
        <LargeRoundedButton
          text="Join from link"
          iconProps={{ iconName: 'JoinFromLink' }}
          onClick={props.onJoinFromLinkClick}
        />
      </Stack.Item>
      <Stack.Item>
        <LargeRoundedButton
          text={loadingSpeakToBot ? undefined : 'Speak to ACS Copilot'}
          iconProps={loadingSpeakToBot ? undefined : { iconName: 'SpeakToBot' }}
          onClick={speakToBotClick}
        >
          {loadingSpeakToBot && <Spinner ariaLabel="Starting Speak To ACS Copilot" />}
        </LargeRoundedButton>
      </Stack.Item>
      <Stack.Item>
        <LargeRoundedButton text="PSTN Call" iconProps={{ iconName: 'MakePSTNCall' }} onClick={props.onPSTNCallClick} />
      </Stack.Item>
    </Stack>
  );
};

const landingPageButtonsContainerStyles: IStackStyles = {
  root: {
    height: '4.0625rem'
  }
};
